import { useRef, useState } from 'react';
import { QRCodeCanvas, QRCodeSVG } from 'qrcode.react';
import { useBrand } from '@/hooks/useBrand';

interface Props {
  title: string;
  subtitle?: string;
  data: string;
  onClose: () => void;
}

export default function QrModal({ title, subtitle, data, onClose }: Props) {
  const { brand } = useBrand();
  const [copied, setCopied] = useState(false);
  const canvasRef = useRef<HTMLDivElement>(null);

  /** Télécharge le QR affiché en image PNG haute définition. */
  const exportImage = () => {
    const canvas = canvasRef.current?.querySelector('canvas');
    if (!canvas) return;
    const link = document.createElement('a');
    const safe = (title || 'qr-code').normalize('NFD').replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-+|-+$/g, '').toLowerCase();
    link.download = `${safe || 'qr-code'}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(data);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-50" onClick={onClose} />
      <div className="fixed z-50 inset-x-0 bottom-0 sm:inset-0 sm:flex sm:items-center sm:justify-center sm:p-4">
        <div className="w-full sm:max-w-sm bg-background-50 rounded-t-2xl sm:rounded-2xl border border-background-200/70">
          <div className="flex items-center justify-between px-5 h-14 border-b border-background-200/70">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="w-9 h-9 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center flex-shrink-0">
                <i className="ri-qr-code-line text-lg"></i>
              </div>
              <div className="min-w-0">
                <h3 className="text-sm font-bold text-foreground-950 truncate">{title}</h3>
                {subtitle && <p className="text-xs text-foreground-500 truncate">{subtitle}</p>}
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-100 transition-colors cursor-pointer flex-shrink-0"
            >
              <i className="ri-close-line text-foreground-500"></i>
            </button>
          </div>

          <div className="p-6 flex flex-col items-center gap-4">
            <div className="p-4 bg-white rounded-2xl border border-background-200/70">
              <QRCodeSVG
                value={data}
                size={196}
                level="H"
                fgColor="#172C43"
                bgColor="#ffffff"
                imageSettings={
                  brand.logo
                    ? { src: brand.logo, height: 48, width: 48, excavate: true }
                    : undefined
                }
              />
            </div>
            <p className="text-xs text-foreground-500 text-center leading-relaxed">
              Faites scanner ce code. Le lien est également partageable.
            </p>
            <div className="w-full flex items-center gap-2 bg-background-100 rounded-lg px-3 py-2">
              <i className="ri-link text-foreground-400 text-sm flex-shrink-0"></i>
              <span className="text-xs text-foreground-600 truncate flex-1">{data}</span>
            </div>
            <button
              onClick={copy}
              className="w-full h-11 rounded-full text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer flex items-center justify-center gap-2 bg-primary-500 text-background-50 hover:bg-primary-600"
            >
              <i className={copied ? 'ri-check-line' : 'ri-file-copy-line'}></i>
              {copied ? 'Lien copié !' : 'Copier le lien'}
            </button>

            <button
              onClick={exportImage}
              className="w-full h-11 rounded-full text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer flex items-center justify-center gap-2 bg-background-100 border border-background-200/70 text-foreground-800 hover:bg-background-200/70"
            >
              <i className="ri-download-2-line"></i>
              Exporter en image
            </button>

            {/* Canvas caché : sert uniquement à générer le PNG exporté. */}
            <div ref={canvasRef} className="hidden" aria-hidden="true">
              <QRCodeCanvas
                value={data}
                size={1024}
                level="H"
                fgColor="#172C43"
                bgColor="#ffffff"
                imageSettings={
                  brand.logo
                    ? { src: brand.logo, height: 240, width: 240, excavate: true }
                    : undefined
                }
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}